import { useEffect, useState } from 'react';

type CreatorDetails = {
  id: string;
  full_name?: string | null;
  brand_name?: string | null;
  profile_photo_url?: string | null;
  country?: string | null;
  city?: string | null;
  primary_niche?: string | null;
  secondary_niches?: string | null;
  short_bio?: string | null;
  primary_website?: string | null;
  best_contact_email?: string | null;
  total_followers?: number | null;
  avg_engagement_rate?: number | null;
  content_type?: string | null;
  influencer_type?: string | null;
  total_views?: string | null;
  languages?: string | null;
};

type Props = {
  creatorId: string;
  onClose: () => void;
};

export default function CreatorCard({ creatorId, onClose }: Props) {
  const [creator, setCreator] = useState<CreatorDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCreator = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/creators/${creatorId}`);
        if (!response.ok) {
          throw new Error('Failed to load creator details');
        }
        const data = await response.json() as { creator: CreatorDetails };
        setCreator(data.creator);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadCreator();
  }, [creatorId]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="w-full max-w-2xl border border-gray-300 bg-white p-8" onClick={(e) => e.stopPropagation()}>
          <div className="animate-pulse space-y-4">
            <div className="h-32 w-32 rounded-full bg-gray-200 mx-auto" />
            <div className="h-6 w-3/4 bg-gray-200 mx-auto" />
            <div className="h-4 w-1/2 bg-gray-200 mx-auto" />
            <div className="space-y-2">
              <div className="h-4 w-full bg-gray-200" />
              <div className="h-4 w-full bg-gray-200" />
              <div className="h-4 w-3/4 bg-gray-200" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !creator) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="w-full max-w-2xl border border-gray-300 bg-white p-8 text-center" onClick={(e) => e.stopPropagation()}>
          <p className="text-red-600">{error || 'Creator not found'}</p>
          <button
            onClick={onClose}
            className="mt-4 bg-black text-waybook-green px-6 py-2 font-semibold hover:bg-gray-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const name = creator.full_name || creator.brand_name || 'Unknown Creator';
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl border border-gray-300 bg-white p-6 sm:p-8 my-8" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="float-right bg-gray-100 p-2 hover:bg-gray-200 transition-colors"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Profile Photo */}
        <div className="mb-6 text-center">
          {creator.profile_photo_url ? (
            <img
              src={creator.profile_photo_url}
              alt={name}
              className="mx-auto h-32 w-32 rounded-full object-cover"
            />
          ) : (
            <div className="mx-auto h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-4xl font-bold text-gray-400">{name.charAt(0)}</span>
            </div>
          )}
        </div>

        {/* Name & Title */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-black mb-1">{name}</h2>
          {creator.brand_name && creator.brand_name !== creator.full_name && (
            <p className="text-lg text-gray-600">{creator.brand_name}</p>
          )}
          <div className="mt-2 flex items-center justify-center gap-2 text-sm text-gray-500">
            {creator.country && <span>{creator.country}</span>}
            {creator.city && <span>• {creator.city}</span>}
          </div>
        </div>

        {/* Bio */}
        {creator.short_bio && (
          <div className="mb-6">
            <p className="text-gray-700 text-center">{creator.short_bio}</p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="mb-6 grid grid-cols-2 gap-4">
          {creator.total_followers !== null && (
            <div className="border border-gray-200 bg-gray-50 p-4 text-center">
              <p className="text-2xl font-bold text-black">{creator.total_followers?.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Followers</p>
            </div>
          )}
          {creator.avg_engagement_rate !== null && creator.avg_engagement_rate !== undefined && (
            <div className="border border-gray-200 bg-gray-50 p-4 text-center">
              <p className="text-2xl font-bold text-black">{((creator.avg_engagement_rate ?? 0) * 100).toFixed(2)}%</p>
              <p className="text-sm text-gray-600">Engagement</p>
            </div>
          )}
          {creator.total_views && (
            <div className="border border-gray-200 bg-gray-50 p-4 text-center">
              <p className="text-2xl font-bold text-black">{creator.total_views}</p>
              <p className="text-sm text-gray-600">Total Views</p>
            </div>
          )}
          {creator.influencer_type && (
            <div className="border border-gray-200 bg-gray-50 p-4 text-center">
              <p className="text-lg font-bold text-black">{creator.influencer_type}</p>
              <p className="text-sm text-gray-600">Type</p>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-3 border-t border-gray-200 pt-4">
          {creator.primary_niche && (
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">Niche:</span>
              <span className="text-gray-600">{creator.primary_niche}</span>
            </div>
          )}
          {creator.content_type && (
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">Content Type:</span>
              <span className="text-gray-600">{creator.content_type}</span>
            </div>
          )}
          {creator.languages && (
            <div className="flex justify-between">
              <span className="font-semibold text-gray-700">Languages:</span>
              <span className="text-gray-600">{creator.languages}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          {creator.primary_website && (
            <a
              href={creator.primary_website.startsWith('http') ? creator.primary_website : `https://${creator.primary_website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-black text-waybook-green px-6 py-3 text-center font-semibold hover:bg-gray-900 transition-colors"
            >
              Visit Website
            </a>
          )}
          {creator.best_contact_email && (
            <a
              href={`mailto:${creator.best_contact_email}`}
              className="flex-1 bg-black text-waybook-green px-6 py-3 text-center font-semibold hover:bg-gray-900 transition-colors"
            >
              Contact
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
